import React, { createContext, useContext, useReducer, ReactNode, useEffect } from 'react';
import { AppState, Calculation, Variable, ChatMessage, CalculatorMode, Session, AppSettings, SessionSettings, User } from '../types';

// Default settings
const defaultAppSettings: AppSettings = {
  theme: 'dark',
  autoSave: true,
  autoClearOnSessionEnd: true,
  maxHistoryItems: 100,
  maxChatMessages: 200,
  showTypingIndicators: true,
  soundEnabled: true,
  notifications: true,
  allowAnonymousUsers: true,
  defaultAnonymousName: 'Anonymous User',
};


// Initial state
const initialState: AppState = {
  users: [],
  currentUser: {
    id: '',
    name: '',
    color: '#3b82f6',
    avatar: '👤',
    isOnline: false,
    isTyping: false,
    isAnonymous: false,
  },
  calculations: [],
  variables: [],
  chatMessages: [],
  calculator: {
    currentExpression: '',
    currentResult: '',
    isError: false,
    errorMessage: '',
    mode: 'basic',
    isLocked: false,
  },
  isMultiplayerMode: false,
  isBotEnabled: false,
  currentSession: null,
  sessionUsers: [],
  settings: defaultAppSettings,
};

// Action types
type AppAction =
  | { type: 'SET_CURRENT_EXPRESSION'; payload: string }
  | { type: 'SET_CURRENT_RESULT'; payload: string }
  | { type: 'SET_ERROR'; payload: { isError: boolean; message: string } }
  | { type: 'SET_MODE'; payload: CalculatorMode }
  | { type: 'ADD_CALCULATION'; payload: Calculation }
  | { type: 'SET_CALCULATIONS'; payload: Calculation[] }
  | { type: 'SET_VARIABLES'; payload: Variable[] }
  | { type: 'ADD_VARIABLE'; payload: Variable }
  | { type: 'REMOVE_VARIABLE'; payload: string }
  | { type: 'ADD_CHAT_MESSAGE'; payload: ChatMessage }
  | { type: 'SET_CHAT_MESSAGES'; payload: ChatMessage[] }
  | { type: 'SET_USER_TYPING'; payload: { userId: string; isTyping: boolean } }
  | { type: 'SET_USER_ONLINE'; payload: { userId: string; isOnline: boolean } }
  | { type: 'TOGGLE_MULTIPLAYER_MODE' }
  | { type: 'TOGGLE_BOT_ENABLED' }
  | { type: 'SET_LOCK'; payload: { isLocked: boolean; lockedBy?: string } }
  | { type: 'APPROVE_CALCULATION'; payload: { calculationId: string; userId: string } }
  | { type: 'ADD_ANNOTATION'; payload: { calculationId: string; annotation: string } }
  | { type: 'CLEAR_ALL' }
  | { type: 'SET_CURRENT_USER'; payload: { id: string; name: string; color: string; avatar: string; isAnonymous?: boolean } }
  | { type: 'CREATE_ANONYMOUS_USER' }
  | { type: 'CREATE_SESSION'; payload: Session }
  | { type: 'JOIN_SESSION'; payload: { session: Session; user: User } }
  | { type: 'LEAVE_SESSION' }
  | { type: 'ADD_SESSION_USER'; payload: User }
  | { type: 'REMOVE_SESSION_USER'; payload: string }
  | { type: 'SET_SESSION_USERS'; payload: User[] }
  | { type: 'UPDATE_APP_SETTINGS'; payload: Partial<AppSettings> }
  | { type: 'UPDATE_SESSION_SETTINGS'; payload: Partial<SessionSettings> }
  | { type: 'CLEAR_HISTORY' }
  | { type: 'CLEAR_CHAT' }
  | { type: 'CLEAR_VARIABLES' };

// Reducer
const appReducer = (state: AppState, action: AppAction): AppState => {
  switch (action.type) {
    case 'SET_CURRENT_EXPRESSION':
      return {
        ...state,
        calculator: {
          ...state.calculator,
          currentExpression: action.payload,
        },
      };

    case 'SET_CURRENT_RESULT':
      return {
        ...state,
        calculator: {
          ...state.calculator,
          currentResult: action.payload,
        },
      };

    case 'SET_ERROR':
      return {
        ...state,
        calculator: {
          ...state.calculator,
          isError: action.payload.isError,
          errorMessage: action.payload.message,
        },
      };

    case 'SET_MODE':
      return {
        ...state,
        calculator: {
          ...state.calculator,
          mode: action.payload,
        },
      };

    case 'ADD_CALCULATION':
      return {
        ...state,
        calculations: [action.payload, ...state.calculations],
      };

    case 'SET_CALCULATIONS':
      return {
        ...state,
        calculations: action.payload,
      };
    case 'SET_VARIABLES':
      return {
        ...state,
        variables: action.payload,
      };

    case 'ADD_VARIABLE':
      return {
        ...state,
        variables: [action.payload, ...state.variables.filter(v => v.name !== action.payload.name)],
      };

    case 'REMOVE_VARIABLE':
      return {
        ...state,
        variables: state.variables.filter(v => v.id !== action.payload),
      };

    case 'ADD_CHAT_MESSAGE':
      return {
        ...state,
        chatMessages: [...state.chatMessages, action.payload],
      };

    case 'SET_CHAT_MESSAGES':
      return {
        ...state,
        chatMessages: action.payload,
      };

    case 'SET_USER_TYPING':
      return {
        ...state,
        users: state.users.map(user =>
          user.id === action.payload.userId
            ? { ...user, isTyping: action.payload.isTyping }
            : user
        ),
      };

    case 'SET_USER_ONLINE':
      return {
        ...state,
        users: state.users.map(user =>
          user.id === action.payload.userId
            ? { ...user, isOnline: action.payload.isOnline }
            : user
        ),
      };

    case 'TOGGLE_MULTIPLAYER_MODE':
      return {
        ...state,
        isMultiplayerMode: !state.isMultiplayerMode,
      };

    case 'TOGGLE_BOT_ENABLED':
      return {
        ...state,
        isBotEnabled: !state.isBotEnabled,
      };

    case 'SET_LOCK':
      return {
        ...state,
        calculator: {
          ...state.calculator,
          isLocked: action.payload.isLocked,
          lockedBy: action.payload.lockedBy,
        },
      };

    case 'APPROVE_CALCULATION':
      return {
        ...state,
        calculations: state.calculations.map(calc =>
          calc.id === action.payload.calculationId
            ? {
                ...calc,
                approvedBy: calc.approvedBy.includes(action.payload.userId)
                  ? calc.approvedBy.filter(id => id !== action.payload.userId)
                  : [...calc.approvedBy, action.payload.userId],
                isApproved: calc.approvedBy.length > 0,
              }
            : calc
        ),
      };

    case 'ADD_ANNOTATION':
      return {
        ...state,
        calculations: state.calculations.map(calc =>
          calc.id === action.payload.calculationId
            ? { ...calc, annotation: action.payload.annotation }
            : calc
        ),
      };

    case 'CLEAR_ALL':
      // Clear from Firebase if in a session
      if (state.currentSession && state.currentUser.id) {
        Promise.all([
          import('../services/calculatorService').then(({ calculatorService }) => 
            calculatorService.clearCalculations(state.currentSession!.id)
          ),
          import('../services/calculatorService').then(({ calculatorService }) => 
            calculatorService.clearVariables(state.currentSession!.id)
          ),
          import('../services/chatService').then(({ chatService }) => 
            chatService.clearMessages(state.currentSession!.id)
          )
        ]).catch(error => {
          console.error('Failed to clear data from Firebase:', error);
        });
      }
      
      return {
        ...state,
        calculations: [],
        variables: [],
        chatMessages: [],
        calculator: {
          ...state.calculator,
          currentExpression: '',
          currentResult: '',
          isError: false,
          errorMessage: '',
        },
      };

    case 'SET_CURRENT_USER':
      return {
        ...state,
        currentUser: {
          ...state.currentUser,
          id: action.payload.id,
          name: action.payload.name,
          color: action.payload.color,
          avatar: action.payload.avatar,
          isOnline: true,
          isAnonymous: action.payload.isAnonymous || false,
        },
      };

    case 'CREATE_ANONYMOUS_USER':
      const anonymousId = `anon_${Date.now()}`;
      const colors = ['#3b82f6', '#ef4444', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16', '#f97316', '#6366f1'];
      const randomColor = colors[Math.floor(Math.random() * colors.length)];
      return {
        ...state,
        currentUser: {
          id: anonymousId,
          name: state.settings.defaultAnonymousName,
          color: randomColor,
          avatar: '👤',
          isOnline: true,
          isTyping: false,
          isAnonymous: true,
        },
      };

    case 'CREATE_SESSION':
      return {
        ...state,
        currentSession: action.payload,
        isMultiplayerMode: true,
        sessionUsers: [], // Let Firebase listeners populate this
        // Apply session settings
        settings: {
          ...state.settings,
          ...action.payload.settings
        }
      };

    case 'JOIN_SESSION':
      return {
        ...state,
        currentSession: action.payload.session,
        isMultiplayerMode: true,
        sessionUsers: [], // Let Firebase listeners populate this
        // Apply session settings
        settings: {
          ...state.settings,
          ...action.payload.session.settings
        }
      };

    case 'LEAVE_SESSION':
      return {
        ...state,
        currentSession: null,
        isMultiplayerMode: false,
        sessionUsers: [],
        calculations: [], // Always clear calculations when leaving session
        chatMessages: [], // Always clear chat when leaving session
        variables: [], // Always clear variables when leaving session
        calculator: {
          ...initialState.calculator,
          currentExpression: '',
          currentResult: '0',
        },
      };

    case 'ADD_SESSION_USER':
      return {
        ...state,
        sessionUsers: [...state.sessionUsers, action.payload],
      };

    case 'REMOVE_SESSION_USER':
      return {
        ...state,
        sessionUsers: state.sessionUsers.filter(user => user.id !== action.payload),
      };

    case 'SET_SESSION_USERS':
      return {
        ...state,
        sessionUsers: action.payload,
      };

    case 'UPDATE_APP_SETTINGS':
      return {
        ...state,
        settings: { ...state.settings, ...action.payload },
      };

    case 'UPDATE_SESSION_SETTINGS':
      // Update local state
      const updatedState = {
        ...state,
        currentSession: state.currentSession ? {
          ...state.currentSession,
          settings: { ...state.currentSession.settings, ...action.payload }
        } : null,
      };
      
      // Save to Firebase if in a session (async, don't block UI)
      if (state.currentSession && state.currentUser.id) {
        import('../services/sessionService').then(({ sessionService }) => {
          sessionService.updateSessionSettings(state.currentSession!.id, action.payload);
        }).catch(error => {
          console.error('Failed to update session settings in Firebase:', error);
        });
      }
      
      return updatedState;

    case 'CLEAR_HISTORY':
      // Clear from Firebase if in a session
      if (state.currentSession && state.currentUser.id) {
        import('../services/calculatorService').then(({ calculatorService }) => {
          calculatorService.clearCalculations(state.currentSession!.id);
        }).catch(error => {
          console.error('Failed to clear calculations from Firebase:', error);
        });
      }
      
      return {
        ...state,
        calculations: [],
      };

    case 'CLEAR_CHAT':
      // Clear from Firebase if in a session
      if (state.currentSession && state.currentUser.id) {
        import('../services/chatService').then(({ chatService }) => {
          chatService.clearMessages(state.currentSession!.id);
        }).catch(error => {
          console.error('Failed to clear messages from Firebase:', error);
        });
      }
      
      return {
        ...state,
        chatMessages: [],
      };

    case 'CLEAR_VARIABLES':
      // Clear from Firebase if in a session
      if (state.currentSession && state.currentUser.id) {
        import('../services/calculatorService').then(({ calculatorService }) => {
          calculatorService.clearVariables(state.currentSession!.id);
        }).catch(error => {
          console.error('Failed to clear variables from Firebase:', error);
        });
      }
      
      return {
        ...state,
        variables: [],
      };

    default:
      return state;
  }
};

// Context
const AppContext = createContext<{
  state: AppState;
  dispatch: React.Dispatch<AppAction>;
} | null>(null);

// Helper functions for localStorage
const saveToLocalStorage = (key: string, data: any) => {
  try {
    localStorage.setItem(key, JSON.stringify(data));
  } catch (error) {
    console.warn('Failed to save to localStorage:', error);
  }
};

const loadFromLocalStorage = (key: string, defaultValue: any) => {
  try {
    const item = localStorage.getItem(key);
    return item ? JSON.parse(item) : defaultValue;
  } catch (error) {
    console.warn('Failed to load from localStorage:', error);
    return defaultValue;
  }
};

// Provider
export const AppProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  // Initialize state with localStorage data
  const [state, dispatch] = useReducer(appReducer, initialState, () => {
    const savedSettings = loadFromLocalStorage('decalculator-settings', defaultAppSettings);
    const savedUser = loadFromLocalStorage('decalculator-user', null);
    
    return {
      ...initialState,
      settings: savedSettings,
      currentUser: savedUser ? {
        ...initialState.currentUser,
        ...savedUser,
        isOnline: true,
      } : initialState.currentUser,
    };
  });

  // Save settings to localStorage when they change
  useEffect(() => {
    saveToLocalStorage('decalculator-settings', state.settings);
  }, [state.settings]);

  // Save user to localStorage when they change
  useEffect(() => {
    if (state.currentUser.id) {
      saveToLocalStorage('decalculator-user', {
        id: state.currentUser.id,
        name: state.currentUser.name,
        color: state.currentUser.color,
        avatar: state.currentUser.avatar,
      });
    }
  }, [state.currentUser.id, state.currentUser.name, state.currentUser.color, state.currentUser.avatar]);

  // Auto-clear functionality
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (state.settings.autoClearOnSessionEnd && state.currentSession) {
        saveToLocalStorage('decalculator-temp-clear', true);
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [state.settings.autoClearOnSessionEnd, state.currentSession]);

  // Check for auto-clear on mount
  useEffect(() => {
    const shouldClear = loadFromLocalStorage('decalculator-temp-clear', false);
    if (shouldClear) {
      dispatch({ type: 'CLEAR_ALL' });
      localStorage.removeItem('decalculator-temp-clear');
    }
  }, []);

  return (
    <AppContext.Provider value={{ state, dispatch }}>
      {children}
    </AppContext.Provider>
  );
};

// Hook
export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
};
